/**
 * Debug utilities - Centralized logging with performance monitoring
 */

// Debug flag - can be toggled via localStorage
const DEBUG_KEY = 'really-assess:debug'
const PERF_KEY = 'really-assess:perf'

/**
 * Check if debug mode is enabled
 */
export function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(DEBUG_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Check if performance monitoring is enabled
 */
export function isPerfEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return localStorage.getItem(PERF_KEY) === 'true'
  } catch {
    return false
  }
}

/**
 * Enable/disable debug mode
 */
export function setDebug(enabled: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (enabled) {
      localStorage.setItem(DEBUG_KEY, 'true')
      console.log('[Debug] Debug mode enabled')
    } else {
      localStorage.removeItem(DEBUG_KEY)
      console.log('[Debug] Debug mode disabled')
    }
  } catch {
    // Ignore
  }
}

/**
 * Enable/disable performance monitoring
 */
export function setPerf(enabled: boolean): void {
  if (typeof window === 'undefined') return
  try {
    if (enabled) {
      localStorage.setItem(PERF_KEY, 'true')
      console.log('[Perf] Performance monitoring enabled')
    } else {
      localStorage.removeItem(PERF_KEY)
      console.log('[Perf] Performance monitoring disabled')
    }
  } catch {
    // Ignore
  }
}

/**
 * Debug logger - only logs when debug mode is enabled
 */
export const debug = {
  log: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      console.log(...args)
    }
  },
  warn: (...args: unknown[]) => {
    if (isDebugEnabled()) {
      console.warn(...args)
    }
  },
  error: (...args: unknown[]) => {
    // Always log errors
    console.error(...args)
  },
  group: (label: string) => {
    if (isDebugEnabled()) {
      console.group(label)
    }
  },
  groupEnd: () => {
    if (isDebugEnabled()) {
      console.groupEnd()
    }
  },
}

/**
 * Performance timer
 */
export class PerfTimer {
  private startTime: number
  private label: string

  constructor(label: string) {
    this.label = label
    this.startTime = performance.now()
    if (isPerfEnabled()) {
      console.log(`[Perf] ${label} - started`)
    }
  }

  end(): number {
    const elapsed = performance.now() - this.startTime
    if (isPerfEnabled()) {
      console.log(`[Perf] ${this.label} - completed in ${elapsed.toFixed(2)}ms`)
    }
    return elapsed
  }

  lap(label?: string): number {
    const elapsed = performance.now() - this.startTime
    if (isPerfEnabled()) {
      const lapLabel = label ? `${this.label} (${label})` : this.label
      console.log(`[Perf] ${lapLabel} - ${elapsed.toFixed(2)}ms`)
    }
    return elapsed
  }
}

/**
 * Measure async operation performance
 */
export async function measureAsync<T>(
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  const timer = new PerfTimer(label)
  try {
    const result = await fn()
    timer.end()
    return result
  } catch (error) {
    timer.end()
    throw error
  }
}

/**
 * Measure sync operation performance
 */
export function measure<T>(label: string, fn: () => T): T {
  const timer = new PerfTimer(label)
  try {
    const result = fn()
    timer.end()
    return result
  } catch (error) {
    timer.end()
    throw error
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  (window as unknown as { reallyAssessDebug?: {
    enable: () => void
    disable: () => void
    enablePerf: () => void
    disablePerf: () => void
  } }).reallyAssessDebug = {
    enable: () => setDebug(true),
    disable: () => setDebug(false),
    enablePerf: () => setPerf(true),
    disablePerf: () => setPerf(false),
  }
}
